/**
 * Apresentação da rotatividade — um relatório limpo, pronto para mostrar ao cliente ou salvar em
 * PDF (o botão dispara a impressão do navegador). Abre em aba própria a partir do botão "Gerar
 * apresentação" da tela de rotatividade.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useParams, useSearchParams } from 'react-router-dom';

import turnoverService from '../../services/turnoverService';
import { companyService } from '../../services/companyService';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import {
  emPorcento,
  tempoLegivel,
  rotuloDoMes,
  type TurnoverDoPeriodo,
  type TempoDeCasa,
  type SetorDetalhado,
  type MesDoTurnover,
  type DesligamentosPorNatureza,
} from '../../types/turnover';

function Metrica({ rotulo, valor, sub }: { rotulo: string; valor: string; sub?: string }): React.ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {rotulo}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>{valor}</Typography>
      {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

/** A carga fica fora do componente para não trocar de identidade a cada render. */
const carregarEmpresas = async () => companyService.getCompaniesForDropdown();

export const TurnoverPresentationPage: React.FC = () => {
  const { companyId = '' } = useParams();
  const [params] = useSearchParams();
  const ano = Number(params.get('ano')) || new Date().getFullYear();

  const [doAno, setDoAno] = useState<TurnoverDoPeriodo | null>(null);
  const [tempo, setTempo] = useState<TempoDeCasa | null>(null);
  const [setores, setSetores] = useState<SetorDetalhado[]>([]);
  const [meses, setMeses] = useState<MesDoTurnover[]>([]);
  const [natureza, setNatureza] = useState<DesligamentosPorNatureza | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  /**
   * O nome da empresa no cabeçalho da apresentação. Vem de `company.read`, que a porta desta
   * tela (`employee.read`) não cobre — por isso passa pelo gancho, e quem não tem a permissão
   * vê a apresentação sem o nome em vez de um alerta vermelho.
   */
  const empresas = useListaPermitida('company.read', 'Empresa', carregarEmpresas);
  const empresa =
    empresas.itens.find((c) => String(c.id) === String(companyId))?.name ?? 'Empresa';

  const carregar = useCallback(async () => {
    try {
      const [periodo, tc, sd, mensal, porNatureza] = await Promise.all([
        turnoverService.doPeriodo(companyId, `${ano}-01-01`, `${ano}-12-31`),
        turnoverService.tempoDeCasa(companyId),
        turnoverService.porSetorDetalhado(companyId, `${ano}-01-01`, `${ano}-12-31`),
        turnoverService.porMes(companyId, ano),
        turnoverService.porNatureza(companyId, `${ano}-01-01`, `${ano}-12-31`),
      ]);
      setDoAno(periodo);
      setTempo(tc);
      setSetores(sd);
      setMeses(mensal);
      setNatureza(porNatureza);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível montar a apresentação.');
    }
  }, [companyId, ano]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const dadosSetor = useMemo(
    () => setores.filter((s) => s.desligamentos > 0).slice(0, 8).map((s) => ({ setor: s.nome, Saídas: s.desligamentos })),
    [setores]
  );
  const dadosMes = useMemo(
    () => meses.map((m) => ({ mes: rotuloDoMes(m.mes), Rotatividade: Math.round(m.turnoverGeral * 100) / 100 })),
    [meses]
  );

  if (erro) {
    return <Box sx={{ p: 4 }}><Alert severity="error">{erro}</Alert></Box>;
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 }, bgcolor: 'background.default' }}>
      <style>{`@media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; } }`}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">Lidera SST · Relatório de Rotatividade</Typography>
        <Button className="no-print" variant="contained" onClick={() => window.print()}>Imprimir / Salvar PDF</Button>
      </Stack>

      <Typography variant="h4" sx={{ fontWeight: 800 }}>{empresa}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Rotatividade — ano de {ano}</Typography>

      {doAno && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Metrica rotulo="Rotatividade geral" valor={emPorcento(doAno.turnoverGeral)} sub="admissões e desligamentos" />
          <Metrica rotulo="Só desligamentos" valor={emPorcento(doAno.turnoverDesligamento)} sub="perda de gente" />
          <Metrica rotulo="Efetivo médio" valor={doAno.efetivoMedio.toLocaleString('pt-BR')} sub={`${doAno.admissoes} admissões · ${doAno.desligamentos} saídas`} />
          {tempo && (
            <Metrica rotulo="Tempo médio de casa" valor={tempoLegivel(tempo.mediaGeralDias)} sub={`ativos ${tempoLegivel(tempo.mediaAtivosDias)}`} />
          )}
          {natureza && natureza.total > 0 && (
            <Metrica
              rotulo="Voluntário × involuntário"
              valor={`${natureza.voluntario} × ${natureza.involuntario}`}
              sub={`de ${natureza.total} saída(s)`}
            />
          )}
        </Stack>
      )}

      {dadosMes.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Rotatividade mês a mês (%)</Typography>
          <Box sx={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={dadosMes} margin={{ left: -8, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={12} />
                <YAxis fontSize={12} />
                <RechartsTooltip />
                <Bar dataKey="Rotatividade" fill="#1976d2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {dadosSetor.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Saídas por setor</Typography>
          <Box sx={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={dadosSetor} layout="vertical" margin={{ left: 24, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={12} />
                <YAxis type="category" dataKey="setor" width={120} fontSize={12} />
                <RechartsTooltip />
                <Bar dataKey="Saídas" fill="#d32f2f" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {setores.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Detalhe por setor</Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={1}>
            <Stack direction="row" sx={{ fontWeight: 700, fontSize: 13 }}>
              <Box sx={{ flex: 2 }}>Setor</Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>Efetivo</Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>Saídas</Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>Rotativ.</Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>Tempo médio</Box>
            </Stack>
            {setores.map((s) => (
              <Stack key={s.departmentId ?? 'sem'} direction="row" sx={{ fontSize: 13 }} alignItems="center">
                <Box sx={{ flex: 2 }}>{s.nome}</Box>
                <Box sx={{ flex: 1, textAlign: 'right' }}>{s.efetivo}</Box>
                <Box sx={{ flex: 1, textAlign: 'right' }}>{s.desligamentos}</Box>
                <Box sx={{ flex: 1, textAlign: 'right' }}>
                  <Chip size="small" variant="outlined" color={s.taxa >= 50 ? 'error' : s.taxa >= 20 ? 'warning' : 'default'} label={emPorcento(s.taxa)} />
                </Box>
                <Box sx={{ flex: 1, textAlign: 'right' }}>{tempoLegivel(s.tempoMedioDias)}</Box>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Gerado em {new Date().toLocaleDateString('pt-BR')} · Rotatividade = (admissões + desligamentos) ÷ 2 ÷ efetivo médio.
      </Typography>
    </Box>
  );
};

export default TurnoverPresentationPage;
